import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { APP_BASE } from "@/lib/paths";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { getMyBilling, getMyInvoices, openInvoicePdf } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { toast } from "sonner";
import { CreditCard, FileText, Download, AlertTriangle, CheckCircle2, Droplets } from "lucide-react";

export const StatusBadge = ({ status }) => {
  const { t } = useT();
  const map = {
    draft: { cls: "bg-secondary text-muted-foreground", key: "billing.statusDraft" },
    sent: { cls: "bg-[hsl(var(--brand)/0.12)] text-[hsl(var(--brand))]", key: "billing.statusSent" },
    paid: { cls: "badge-ok", key: "billing.statusPaid" },
    overdue: { cls: "badge-bad", key: "billing.statusOverdue" },
  };
  const m = map[status] || map.draft;
  return <Badge className={m.cls} data-testid={`invoice-status-${status}`}>{t(m.key)}</Badge>;
};

const money = (n, cur) => `${Number(n || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur || "RON"}`;

export default function Billing() {
  const { user, ready } = useAuth();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState(null);

  const load = async () => {
    try {
      const [b, inv] = await Promise.all([getMyBilling(), getMyInvoices()]);
      setData(b);
      setInvoices(inv);
    } catch {
      toast.error(t("billing.loadError"));
      setData(false);
      setInvoices([]);
    }
  };

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "manager")) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (ready && (!user || !["admin", "manager"].includes(user.role))) {
    return <Navigate to={APP_BASE} replace />;
  }

  const plan = data?.plan;
  const summary = data?.summary || {};
  const locked = data?.organization?.billing_locked;
  const usage = data?.milk_usage;
  const unpaidCurrency = (invoices || []).find((i) => i.status === "sent" || i.status === "overdue")?.currency || "RON";
  const usagePct = usage && usage.limit > 0 ? Math.min(100, Math.round((usage.used / usage.limit) * 100)) : 0;

  const download = async (id) => {
    try { await openInvoicePdf(`/billing/invoices/${id}/pdf`); }
    catch { toast.error(t("billing.actionError")); }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="billing-page">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t("billing.pageTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("billing.pageSubtitle")}</p>
        </div>

        {locked && (
          <div
            className="flex items-start gap-3 rounded-lg border border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger)/0.08)] px-4 py-3 text-sm"
            data-testid="billing-locked-banner"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--danger))]" />
            <span className="text-[hsl(var(--danger))]">{t("billing.lockedBanner")}</span>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card data-testid="billing-plan-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <CreditCard className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("billing.currentPlan")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data === null ? (
                <Skeleton className="h-8 w-32" />
              ) : plan ? (
                <>
                  <div className="text-2xl font-semibold">{plan.name}</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    {money(plan.price_ron, "RON")} · {money(plan.price_eur, "EUR")}
                    <span>{plan.interval === "yearly" ? t("billing.yearly") : t("billing.monthly")}</span>
                  </div>
                  {plan.features && <div className="mt-2 text-xs text-muted-foreground">{plan.features}</div>}
                </>
              ) : (
                <div className="text-sm text-muted-foreground">{t("billing.noPlan")}</div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="billing-summary-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("billing.outstanding")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data === null ? (
                <Skeleton className="h-8 w-32" />
              ) : summary.outstanding > 0 ? (
                <div className="text-2xl font-semibold text-[hsl(var(--danger))]" data-testid="billing-outstanding">
                  {money(summary.outstanding, unpaidCurrency)}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-[hsl(var(--success))]">
                  <CheckCircle2 className="h-4 w-4" /> {t("billing.allPaid")}
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="billing-milk-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Droplets className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("billing.milkUsage")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data === null ? (
                <Skeleton className="h-8 w-32" />
              ) : usage && usage.limit > 0 ? (
                <>
                  <div className="text-sm font-medium" data-testid="billing-milk-usage">
                    {usage.used.toLocaleString("ro-RO")} / {usage.limit.toLocaleString("ro-RO")} L
                  </div>
                  <Progress value={usagePct} className="mt-2 h-2" />
                  <div className="mt-1 text-xs text-muted-foreground">{usagePct}% · {t("billing.thisMonth")}</div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground" data-testid="billing-milk-usage">
                  {usage ? `${usage.used.toLocaleString("ro-RO")} L · ${t("billing.unlimited")}` : t("billing.unlimited")}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("billing.myInvoices")}</CardTitle>
          </CardHeader>
          <CardContent>
            {invoices === null ? (
              <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
            ) : invoices.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">{t("billing.empty")}</p>
            ) : (
              <Table data-testid="billing-invoices-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("billing.number")}</TableHead>
                    <TableHead>{t("billing.issueDate")}</TableHead>
                    <TableHead>{t("billing.dueDate")}</TableHead>
                    <TableHead className="text-right">{t("billing.amount")}</TableHead>
                    <TableHead>{t("billing.status")}</TableHead>
                    <TableHead className="text-right">{t("billing.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.id} data-testid={`billing-invoice-${inv.id}`}>
                      <TableCell className="font-mono text-xs">{inv.number}</TableCell>
                      <TableCell className="text-sm">{inv.issue_date}</TableCell>
                      <TableCell className="text-sm">{inv.due_date}</TableCell>
                      <TableCell className="text-right font-medium">{money(inv.amount, inv.currency)}</TableCell>
                      <TableCell><StatusBadge status={inv.status} /></TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => download(inv.id)} data-testid={`billing-pdf-${inv.id}`}>
                          <Download className="mr-1.5 h-3.5 w-3.5" /> {t("billing.downloadPdf")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
